import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, Check } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { accountRequest } from "@/accountApi";

const FieldLabel = ({ children, idx, hint }) => (
    <div className="flex items-end justify-between mb-2">
        <label className="text-sm font-medium text-ink">{children}</label>
        <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
            {hint ?? String(idx).padStart(2, "0")}
        </span>
    </div>
);

const strengthFor = (pwd) => {
    if (!pwd) return { score: 0, label: "-" };
    let s = 0;
    if (pwd.length >= 8) s += 1;
    if (/[A-Z]/.test(pwd)) s += 1;
    if (/\d/.test(pwd)) s += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) s += 1;
    const labels = ["Weak", "Weak", "Okay", "Strong", "Excellent"];
    return { score: s, label: labels[s] };
};

export default function Register() {
    const [showPwd, setShowPwd] = useState(false);
    const [pwd, setPwd] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const strength = strengthFor(pwd);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        const data = new FormData(e.currentTarget);
        try {
            await accountRequest("/api/account/register", {
                method: "POST",
                body: {
                    name: data.get("name"),
                    company_name: data.get("company"),
                    email: data.get("email"),
                    password: data.get("password"),
                    terms: data.get("terms") === "on",
                },
            });
            toast.success("Account created", {
                description: "Check your email to verify the account before viewing licence details.",
            });
            navigate("/dashboard", { replace: true });
        } catch (error) {
            toast.error("Could not create account", {
                description:
                    error instanceof Error
                        ? error.message
                        : "Check the details and try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Create account / Customer area"
            title="Create your customer account."
            subtitle="Use the same email address you used, or will use, at Stripe checkout."
            sideTitle="A simple account area for licences and billing."
            sideBody="Your account lets you view the licence linked to your checkout email, manage billing, and download the latest Windows build."
            sideMeta={[
                { k: "1-5", v: "computers" },
                { k: "Stripe", v: "checkout" },
                { k: "BYO", v: "SMTP" },
            ]}
        >
            <form data-testid="register-form" onSubmit={onSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                        <FieldLabel idx={1}>Full name</FieldLabel>
                        <input
                            data-testid="register-input-name"
                            name="name"
                            type="text"
                            required
                            autoComplete="name"
                            className="input-sharp"
                            placeholder="Jane Smith"
                        />
                    </div>
                    <div>
                        <FieldLabel idx={2}>Company</FieldLabel>
                        <input
                            data-testid="register-input-company"
                            name="company"
                            type="text"
                            className="input-sharp"
                            placeholder="Acme Ltd."
                        />
                    </div>
                </div>

                <div>
                    <FieldLabel idx={3}>Work email</FieldLabel>
                    <input
                        data-testid="register-input-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        className="input-sharp"
                        placeholder="jane@company.com"
                    />
                </div>

                <div>
                    <FieldLabel idx={4} hint={strength.label.toUpperCase()}>
                        <span>Password</span>
                    </FieldLabel>
                    <div className="relative">
                        <input
                            data-testid="register-input-password"
                            name="password"
                            type={showPwd ? "text" : "password"}
                            required
                            minLength={8}
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                            autoComplete="new-password"
                            className="input-sharp pr-12"
                            placeholder="At least 8 characters"
                        />
                        <button
                            type="button"
                            data-testid="register-toggle-password"
                            onClick={() => setShowPwd((v) => !v)}
                            aria-label={showPwd ? "Hide password" : "Show password"}
                            className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center text-ink-mute hover:text-ink transition-colors"
                        >
                            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                    <div className="mt-2 flex gap-1" aria-hidden data-testid="register-strength">
                        {[0, 1, 2, 3].map((i) => (
                            <span
                                key={i}
                                className={`h-1 flex-1 ${i < strength.score ? "bg-forge" : "bg-border"}`}
                            />
                        ))}
                    </div>
                </div>

                <label
                    data-testid="register-terms"
                    className="flex items-start gap-3 text-sm text-ink-soft cursor-pointer pt-1"
                >
                    <input
                        type="checkbox"
                        name="terms"
                        required
                        className="mt-0.5 h-4 w-4 border border-ink accent-ink shrink-0"
                    />
                    <span>
                        I agree to the <a href="/terms" className="text-ink link-underline font-medium">Terms of use</a>{" "}
                        and <a href="/privacy" className="text-ink link-underline font-medium">Privacy notice</a>.
                    </span>
                </label>

                <button
                    type="submit"
                    data-testid="register-submit"
                    disabled={submitting}
                    className="btn btn-accent w-full h-12 disabled:opacity-70"
                >
                    {submitting ? "Creating account..." : "Create account"}
                    <ArrowRight size={14} />
                </button>

                <ul className="pt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-ink-mute">
                    {["Secure password", "Stripe billing", "Licence dashboard"].map((b) => (
                        <li key={b} className="flex items-center gap-2">
                            <Check size={12} className="text-forge" strokeWidth={3} />
                            {b}
                        </li>
                    ))}
                </ul>
            </form>

            <div className="mt-10 pt-6 border-t border-border flex items-center justify-between gap-4 flex-wrap">
                <span className="text-sm text-ink-soft">Already have an account?</span>
                <Link
                    to="/login"
                    data-testid="register-link-login"
                    className="btn btn-secondary h-10 px-4 text-xs"
                >
                    Sign in
                    <ArrowRight size={12} />
                </Link>
            </div>
        </AuthShell>
    );
}
